import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { FaPaypal, FaCcStripe, FaCcVisa, FaCcMastercard } from "react-icons/fa";
import { SiKlarna, SiAfterpay } from "react-icons/si";
import { Truck } from "lucide-react";

export default function CheckoutPage() {
  const { items, subtotal, clear, discount, discountAmount, total, setDiscount } = useCart();
  const navigate = useNavigate();
  const [method, setMethod] = useState("stripe");
  const [submitting, setSubmitting] = useState(false);
  const [paypalClientId, setPaypalClientId] = useState("");
  const [customer, setCustomer] = useState({
    name: "", email: "", phone: "", address: "", city: "", country: "", notes: "",
  });

  useEffect(() => {
    api.get(`/config?t=${Date.now()}`).then(r => {
      setPaypalClientId(r.data.paypal_client_id || "");
    });
  }, []);

  const setF = (k) => (e) => setCustomer({ ...customer, [k]: e.target.value });

  const validate = () => {
    if (!customer.name || !customer.email || !customer.address) {
      toast.error("Please fill name, email and address.");
      return false;
    }
    if (items.length === 0) {
      toast.error("Your bag is empty.");
      return false;
    }
    return true;
  };

  const handleStripe = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await api.post("/checkout/stripe", {
        items, customer,
        origin_url: window.location.origin,
        payment_method: "stripe",
        discount_code: discount || null,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
      setSubmitting(false);
    }
  };

  const handleCOD = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await api.post("/checkout/cod", {
        items, customer,
        origin_url: window.location.origin,
        payment_method: "cod",
        discount_code: discount || null,
      });
      clear();
      navigate(`/checkout/success?order_id=${res.data.order_id}&method=cod`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Order failed.");
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="checkout-empty" className="max-w-[800px] mx-auto px-6 md:px-12 py-24 text-center">
        <h1 className="font-serif text-4xl">Your bag is empty</h1>
        <p className="text-brand-muted mt-3">Add a piece before checking out.</p>
        <Link to="/shop" className="btn-primary mt-8 inline-block">Shop now</Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="max-w-[1200px] mx-auto px-6 md:px-12 py-12 md:py-20">
      <div className="overline">Checkout</div>
      <h1 className="font-serif text-4xl md:text-5xl mt-3 tracking-tight">Complete your order</h1>

      <div className="grid md:grid-cols-[1fr_400px] gap-12 mt-12">
        <div className="space-y-8">
          <section>
            <div className="overline mb-4">Shipping details</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name" value={customer.name} onChange={setF("name")} testid="checkout-name" required />
              <Field label="Email" type="email" value={customer.email} onChange={setF("email")} testid="checkout-email" required />
              <Field label="Phone" value={customer.phone} onChange={setF("phone")} testid="checkout-phone" />
              <Field label="Country" value={customer.country} onChange={setF("country")} testid="checkout-country" />
              <div className="sm:col-span-2">
                <Field label="Street address" value={customer.address} onChange={setF("address")} testid="checkout-address" required />
              </div>
              <Field label="City" value={customer.city} onChange={setF("city")} testid="checkout-city" />
              <div className="sm:col-span-2">
                <label className="overline block mb-2">Order notes</label>
                <textarea
                  value={customer.notes}
                  onChange={setF("notes")}
                  data-testid="checkout-notes"
                  rows={3}
                  className="w-full border border-brand-line bg-brand-bg px-4 py-3 text-sm focus:outline-none focus:border-brand-ink"
                />
              </div>
            </div>
          </section>

          <section>
            <div className="overline mb-4">Payment method</div>
            <div className="space-y-3">
              <MethodOption
                checked={method === "stripe"}
                onClick={() => setMethod("stripe")}
                testid="method-stripe"
                icon={
                  <div className="flex items-center gap-1.5 text-brand-ink">
                    <FaCcVisa size={20} />
                    <FaCcMastercard size={20} />
                    <SiKlarna size={18} className="text-[#FFA8CD]" />
                    <SiAfterpay size={18} />
                  </div>
                }
                title="Card · Klarna · Clearpay"
                subtitle="Visa, Mastercard, Amex · Buy now, pay later"
              />
              <MethodOption
                checked={method === "paypal"}
                onClick={() => setMethod("paypal")}
                testid="method-paypal"
                icon={<FaPaypal size={22} />}
                title="PayPal"
                subtitle={paypalClientId ? "Pay with your PayPal account" : "Enable PayPal in admin settings to use this method"}
                disabled={!paypalClientId}
              />
              <MethodOption
                checked={method === "cod"}
                onClick={() => setMethod("cod")}
                testid="method-cod"
                icon={<Truck size={22} />}
                title="Cash on delivery"
                subtitle="Pay when your order arrives"
              />
            </div>
          </section>
        </div>

        <aside className="bg-brand-bg2 p-8 self-start">
          <div className="overline mb-4">Order summary</div>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {items.map((i) => (
              <div key={`${i.product_id}-${i.size}`} className="flex justify-between text-sm">
                <div>
                  <div>{i.name}</div>
                  <div className="text-xs text-brand-muted">{i.size} · qty {i.quantity}</div>
                </div>
                <div>£{(i.price * i.quantity).toFixed(2)}</div>
              </div>
            ))}
          </div>

          {discount && (
            <div className="border-t border-brand-line mt-6 pt-4 space-y-2 text-sm">
              <div className="flex justify-between text-brand-muted">
                <span>Subtotal</span>
                <span>£{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-brand-accent" data-testid="checkout-discount">
                <span className="flex items-center gap-2">
                  Code {discount}
                  <button onClick={() => setDiscount("")} className="text-xs underline text-brand-muted hover:text-destructive">remove</button>
                </span>
                <span>−£{discountAmount.toFixed(2)}</span>
              </div>
            </div>
          )}

          <div className={`border-t border-brand-line ${discount ? "mt-4" : "mt-6"} pt-6 flex justify-between items-baseline`}>
            <span className="overline">Total</span>
            <span className="font-serif text-3xl" data-testid="checkout-total">£{(discount ? total : subtotal).toFixed(2)}</span>
          </div>

          <div className="mt-6 space-y-3">
            {method === "stripe" && (
              <button onClick={handleStripe} disabled={submitting} className="btn-primary w-full" data-testid="pay-stripe-btn">
                {submitting ? "Redirecting…" : `Pay £${(discount ? total : subtotal).toFixed(2)}`}
              </button>
            )}
            {method === "cod" && (
              <button onClick={handleCOD} disabled={submitting} className="btn-primary w-full" data-testid="pay-cod-btn">
                {submitting ? "Placing…" : "Place order"}
              </button>
            )}
            {method === "paypal" && paypalClientId && (
              <PayPalSection
                clientId={paypalClientId}
                items={items}
                customer={customer}
                amount={discount ? total : subtotal}
                discountCode={discount || null}
                validate={validate}
                onSuccess={(orderId) => { clear(); navigate(`/checkout/success?order_id=${orderId}&method=paypal`); }}
              />
            )}
            {method === "paypal" && !paypalClientId && (
              <div className="text-xs text-brand-muted text-center py-4" data-testid="paypal-disabled-msg">
                PayPal not yet configured. Add PAYPAL_CLIENT_ID to enable.
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid, required }) {
  return (
    <div>
      <label className="overline block mb-2">{label}{required && " *"}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full border border-brand-line bg-brand-bg px-4 py-3 text-sm focus:outline-none focus:border-brand-ink"
      />
    </div>
  );
}

function MethodOption({ checked, onClick, testid, icon, title, subtitle, disabled }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      data-testid={testid}
      disabled={disabled}
      className={`w-full flex items-center gap-4 p-4 border transition-colors text-left ${
        checked ? "border-brand-ink bg-brand-bg" : "border-brand-line bg-brand-bg"
      } ${disabled ? "opacity-60 cursor-not-allowed" : "hover:border-brand-ink"}`}
    >
      <div className={`w-4 h-4 rounded-full border ${checked ? "border-brand-accent" : "border-brand-line"} flex items-center justify-center`}>
        {checked && <div className="w-2 h-2 rounded-full bg-brand-accent" />}
      </div>
      <div className="text-brand-ink">{icon}</div>
      <div className="flex-1">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-brand-muted">{subtitle}</div>
      </div>
    </button>
  );
}

function PayPalSection({ clientId, items, customer, amount, discountCode, validate, onSuccess }) {
  return (
    <PayPalScriptProvider options={{ clientId, currency: "GBP", intent: "capture" }}>
      <PayPalButtons
        style={{ layout: "vertical", color: "gold", shape: "rect" }}
        forceReRender={[amount, customer.email]}
        createOrder={(_data, actions) => {
          if (!validate()) return Promise.reject(new Error("Form incomplete"));
          return actions.order.create({
            purchase_units: [{ amount: { value: amount.toFixed(2), currency_code: "GBP" } }],
          }).catch((err) => {
            console.error("PayPal createOrder error:", err);
            toast.error(`Order creation failed: ${err?.message || err}`);
            throw err;
          });
        }}
        onApprove={async (data, actions) => {
          try {
            await actions.order.capture();
            const res = await api.post("/checkout/paypal/record", {
              order_id: data.orderID, items, customer, amount, discount_code: discountCode,
            });
            onSuccess(res.data.order_id);
          } catch (err) {
            console.error("PayPal capture error:", err);
            toast.error(`PayPal capture failed: ${err?.message || err}`);
          }
        }}
        onError={(err) => {
          console.error("PayPal error:", err);
          const msg = err?.message || err?.toString() || "Unknown PayPal error";
          toast.error(`PayPal: ${msg.substring(0, 180)}`);
        }}
        onCancel={() => toast.info("PayPal payment cancelled")}
      />
    </PayPalScriptProvider>
  );
}
